import { Skeleton } from '@shared/components/Skeleton';
import { ExpressCheckoutElement, ExpressCheckoutElementProps } from '@stripe/react-stripe-js';
import { AvailablePaymentMethods, StripeExpressCheckoutElementReadyEvent } from '@stripe/stripe-js';
import { Box } from '@withjoy/joykit';
import React, { ReactNode, useCallback, useEffect, useMemo, useState } from 'react';
import { getLocalStorage } from '../storage';

interface ExpressCheckoutElementParentClickEvent {
  availablePaymentMethods?: AvailablePaymentMethods | null;
}

type LoadingExpressCheckoutElementProps = ExpressCheckoutElementProps & {
  /** Use when you want to postpone loading the element (e.g. until a feature flag is ready) */
  waitToLoad?: boolean;

  onParentClick?: (event: ExpressCheckoutElementParentClickEvent) => void;
  disablePaymentEvents?: boolean;
  /** Show fallback content regardless of available payment methods */
  showFallback?: boolean;
  /** Content to display when no payment methods are available  */
  fallbackContent?: ReactNode;
  /** Content to display above payment button when available */
  upperContent?: ReactNode;
  /** Content to display below payment button when available  */
  lowerContent?: ReactNode;
};

const DISABLED_ALL_PAYMENT_METHODS: AvailablePaymentMethods = { amazonPay: false, applePay: false, googlePay: false, link: false, paypal: false, klarna: false };

const EXPRESS_CHECKOUT_LAST_FAILED_DETECTION_AT_STORAGE_KEY = 'expressCheckout.lastFailedDetectionAt';
const EXPRESS_CHECKOUT_LAST_AVAILABLE_PAYMENT_METHODS_STORAGE_KEY = 'expressCheckout.lastAvailablePaymentMethods';
const localStorage = getLocalStorage();

export const availablePaymentMethodsToArray = (paymentMethods?: AvailablePaymentMethods | null) => {
  return paymentMethods
    ? Object.entries(paymentMethods)
        .filter(([, value]) => value)
        .map(([key]) => key)
    : [];
};

export const getLastAvailablePaymentMethods = (): AvailablePaymentMethods | null => {
  const lastAvailablePaymentMethods = localStorage.getItem(EXPRESS_CHECKOUT_LAST_AVAILABLE_PAYMENT_METHODS_STORAGE_KEY);
  return lastAvailablePaymentMethods ? JSON.parse(lastAvailablePaymentMethods) : null;
};

export const isSomePaymentMethodAvailable = (availablePaymentMethods: AvailablePaymentMethods) => Object.values(availablePaymentMethods).some(value => value === true);

export const LoadingExpressCheckoutElement: React.VFC<LoadingExpressCheckoutElementProps> = ({ onLoadError, onReady, ...props }) => {
  const [loading, setLoading] = useState(true);
  const [availablePaymentMethods, setAvailablePaymentMethods] = useState<AvailablePaymentMethods | null>(null);
  const isWithinFailedDetectionPeriod = useMemo(() => {
    const lastFailedDetectionAt = localStorage.getItem(EXPRESS_CHECKOUT_LAST_FAILED_DETECTION_AT_STORAGE_KEY);
    const timeElapsedSinceLastFailedDetection = lastFailedDetectionAt ? Date.now() - Number.parseInt(lastFailedDetectionAt) : null;
    // when last failed detection happened in less than 24 hours
    return timeElapsedSinceLastFailedDetection && timeElapsedSinceLastFailedDetection < 24 * 60 * 60 * 1000;
  }, []);
  const handleReady = useCallback(
    (event: StripeExpressCheckoutElementReadyEvent) => {
      setLoading(false);
      setAvailablePaymentMethods(event.availablePaymentMethods ?? DISABLED_ALL_PAYMENT_METHODS);
      onReady?.(event);
    },
    [setLoading, setAvailablePaymentMethods, onReady]
  );
  const handleLoadError = useCallback(
    event => {
      setLoading(false);
      setAvailablePaymentMethods(DISABLED_ALL_PAYMENT_METHODS);
      onLoadError?.(event);
    },
    [setLoading, setAvailablePaymentMethods, onLoadError]
  );

  useEffect(() => {
    if (isWithinFailedDetectionPeriod) {
      // simulate ready event from stripe
      handleReady({ elementType: 'expressCheckout', availablePaymentMethods: DISABLED_ALL_PAYMENT_METHODS });
    }
  }, [isWithinFailedDetectionPeriod, handleReady]);

  const showFallback = useMemo(() => {
    // show fallback content when decided by upper component or when it's in a failed detection period
    if (props.showFallback || isWithinFailedDetectionPeriod) {
      return true;
    }

    // load the ExpressCheckoutElement to detect avaiable payment methods
    if (!availablePaymentMethods) {
      return false;
    }

    // continue to show the ExpressCheckoutElement when payment methods are available
    if (isSomePaymentMethodAvailable(availablePaymentMethods)) {
      localStorage.setItem(EXPRESS_CHECKOUT_LAST_AVAILABLE_PAYMENT_METHODS_STORAGE_KEY, JSON.stringify(availablePaymentMethods));
      return false;
    }

    // payment methods are not available
    localStorage.setItem(EXPRESS_CHECKOUT_LAST_FAILED_DETECTION_AT_STORAGE_KEY, Date.now().toString());
    return true;
  }, [availablePaymentMethods, props.showFallback, isWithinFailedDetectionPeriod]);
  const loadingButton = <Skeleton height={`${props.options?.buttonHeight ?? 42}px`} width={'100%'} borderRadius={'25px'} />;

  if (props.waitToLoad) {
    return loadingButton;
  }

  return (
    <>
      {showFallback ? (
        props.fallbackContent
      ) : (
        <>
          {loading && loadingButton}
          {!loading && props.upperContent}
          <Box onClick={() => props.onParentClick?.({ availablePaymentMethods })} _hover={{ cursor: props.onParentClick ? 'pointer' : null }} position={'relative'}>
            {props.disablePaymentEvents && <Box width={'100%'} height={'100%'} position={'absolute'} zIndex={1} _hover={{ background: 'rgba(255,255,255,0.3)' }} />}
            <Box pointerEvents={props.disablePaymentEvents ? 'none' : null}>
              <ExpressCheckoutElement {...props} onReady={handleReady} onLoadError={handleLoadError} />
            </Box>
          </Box>
          {!loading && props.lowerContent}
        </>
      )}
    </>
  );
};
